// uiarc: date-picker tetikleyicisi + calendar başlığı (https://uiarc.dev/r/date-picker.json) üzerine kurulu ay seçici.
// uiarc'ta ay seçici yok; tetikleyici (takvim simgesi, değer, dönen ok) ve panel (başlıkta gezinme düğmeleri,
// seçili diski süzülen ızgara, yön kayması) date-picker/calendar'dan alındı. Değer metni seçimin yönüne göre döner.
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'motion/react'
import { useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { todayAsDateInput } from '@/lib/format'
import { messages } from '@/lib/messages'
import { motionTokens } from '@/lib/motion'
import { periodLabel } from '@/lib/period'
import { cn } from '@/lib/utils'

interface MonthPickerProps {
  id?: string
  /** `YYYY-MM`. */
  value: string | undefined
  onChange: (period: string) => void
}

const { blur, duration, ease, spring } = motionTokens

/** Yıl ve değer: ileri bir tarih aşağıdan yükselir, geri olan yukarıdan iner. */
const roll: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    y: `${direction * 0.35}em`,
    filter: `blur(${blur.soft}px)`,
  }),
  center: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: duration.standard, ease: ease.enter },
  },
  exit: (direction: number) => ({
    opacity: 0,
    y: `${direction * -0.3}em`,
    filter: `blur(${blur.subtle}px)`,
    transition: { duration: 0.14, ease: ease.standard },
  }),
}
const rollFade: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: duration.instant } },
  exit: { opacity: 0, transition: { duration: duration.instant } },
}
/** Ay ızgarası yıl yönünde kayar; çıkan ızgara tıklanamaz. */
const gridSlide: Variants = {
  enter: (direction: number) => ({ opacity: 0, x: direction * 28 }),
  center: {
    opacity: 1,
    x: 0,
    transition: { x: spring.smooth, opacity: { duration: 0.22, ease: ease.enter } },
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction * -28,
    transition: { x: spring.smooth, opacity: { duration: 0.14, ease: ease.standard } },
  }),
}

const navButton =
  'text-text-secondary grid size-8 cursor-pointer place-items-center rounded-pill border-0 bg-transparent [transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),scale_var(--duration-spring)_var(--ease-spring)] hover:bg-surface-muted hover:text-foreground active:scale-[0.96] active:[transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),scale_100ms_var(--ease-standard)] motion-reduce:transition-none motion-reduce:active:scale-100'

/** Dönem (`YYYY-MM`) seçici: Popover içinde yıl gezintisi + 12 ay ızgarası. */
export function MonthPicker({ id, value, onChange }: MonthPickerProps) {
  const reduce = useReducedMotion() ?? false
  const [open, setOpen] = useState(false)
  const [year, setYear] = useState(() => Number((value ?? todayAsDateInput()).slice(0, 4)))
  const [direction, setDirection] = useState(1)
  const selectedYear = value ? Number(value.slice(0, 4)) : undefined

  // Seçilen değer (tetikleyici metni) seçimin yönünde döner.
  const [previous, setPrevious] = useState(value)
  const [valueDirection, setValueDirection] = useState(1)
  if (previous !== value) {
    setPrevious(value)
    setValueDirection(!previous || !value || value >= previous ? 1 : -1)
  }

  const goTo = (next: number) => {
    setDirection(next > year ? 1 : -1)
    setYear(next)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setDirection(0)
          setYear(Number((value ?? todayAsDateInput()).slice(0, 4)))
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger
        id={id}
        className={cn(
          'group/trigger border-border bg-surface text-foreground rounded-control relative flex min-h-(--control-height-md) w-44 min-w-0 cursor-pointer items-center gap-[9px] border px-3 text-left text-sm',
          '[transition:border-color_var(--duration-fast)_var(--ease-standard),background-color_var(--duration-fast)_var(--ease-standard)] motion-reduce:transition-none',
          'hover:bg-surface-muted hover:border-border-strong aria-expanded:border-border-strong aria-expanded:bg-surface-muted active:border-border-strong active:bg-surface-muted',
        )}
      >
        <CalendarDays
          className="text-text-muted size-4 flex-none"
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span className="sr-only">{value ? periodLabel(value) : messages.period.placeholder}</span>
        <span className="relative flex min-w-0 flex-1" aria-hidden="true">
          <AnimatePresence mode="popLayout" initial={false} custom={valueDirection}>
            <motion.span
              key={value ?? 'placeholder'}
              className={cn('whitespace-nowrap', !value && 'text-text-muted')}
              custom={valueDirection}
              variants={reduce ? rollFade : roll}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {value ? periodLabel(value) : messages.period.placeholder}
            </motion.span>
          </AnimatePresence>
        </span>
        <ChevronDown
          className="text-text-muted size-4 flex-none [transition:transform_var(--duration-spring)_var(--ease-spring)] group-aria-expanded/trigger:rotate-180 motion-reduce:transition-none"
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 gap-3.5 p-4">
        <div className="grid min-h-8.5 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1">
          <button
            type="button"
            className={navButton}
            aria-label={messages.period.previousYear}
            onClick={() => goTo(year - 1)}
          >
            <ChevronLeft className="size-4" strokeWidth={1.75} aria-hidden="true" />
          </button>
          <span
            className="leading-body grid justify-items-center text-base font-medium tabular-nums"
            aria-live="polite"
          >
            <AnimatePresence mode="popLayout" initial={false} custom={direction}>
              <motion.span
                key={year}
                className="col-start-1 row-start-1 inline-block"
                custom={direction}
                variants={reduce ? rollFade : roll}
                initial="enter"
                animate="center"
                exit="exit"
              >
                {year}
              </motion.span>
            </AnimatePresence>
          </span>
          <button
            type="button"
            className={navButton}
            aria-label={messages.period.nextYear}
            onClick={() => goTo(year + 1)}
          >
            <ChevronRight className="size-4" strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
        <div className="relative grid overflow-hidden *:col-start-1 *:row-start-1">
          <AnimatePresence initial={false} custom={direction}>
            <MonthGrid
              key={year}
              year={year}
              selected={selectedYear === year ? value : undefined}
              direction={direction}
              reduced={reduce}
              onPick={(period) => {
                onChange(period)
                setOpen(false)
              }}
            />
          </AnimatePresence>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function MonthGrid({
  year,
  selected,
  direction,
  reduced,
  onPick,
}: {
  year: number
  selected: string | undefined
  direction: number
  reduced: boolean
  onPick: (period: string) => void
}) {
  return (
    <motion.div
      className="grid grid-cols-3 gap-1"
      custom={direction}
      variants={reduced ? rollFade : gridSlide}
      initial="enter"
      animate="center"
      exit="exit"
    >
      {messages.period.months.map((name, index) => {
        const period = `${year}-${String(index + 1).padStart(2, '0')}`
        const active = period === selected
        return (
          <button
            key={period}
            type="button"
            aria-pressed={active}
            className={cn(
              'relative grid h-9 cursor-pointer place-items-center rounded-xl border-0 bg-transparent px-1 text-sm [-webkit-tap-highlight-color:transparent]',
              '[transition:color_var(--duration-fast)_var(--ease-standard),background-color_var(--duration-fast)_var(--ease-standard),scale_var(--duration-spring)_var(--ease-spring)]',
              'not-aria-pressed:hover:bg-surface-muted active:scale-[0.96] active:[transition:color_var(--duration-fast)_var(--ease-standard),background-color_var(--duration-fast)_var(--ease-standard),scale_100ms_var(--ease-standard)]',
              'aria-pressed:text-control-glyph aria-pressed:font-medium motion-reduce:transition-none motion-reduce:active:scale-100',
            )}
            onClick={() => onPick(period)}
          >
            {active && (
              <motion.span
                layoutId="month-picker-selected"
                className="bg-control-on absolute inset-0 rounded-xl"
                transition={reduced ? { duration: 0 } : spring.morph}
              />
            )}
            <span className="relative">{name}</span>
          </button>
        )
      })}
    </motion.div>
  )
}
